import {
  ArrowTopRightOnSquareIcon,
  AcademicCapIcon,
} from "@heroicons/react/24/outline";
import { Badge, Card } from "@mindstonehq/ui";
import { formatDate } from "~/utils";
import { useUser } from "~/hooks/context-hook";
import { AchievementDetailedFragment } from "~/graphql/codegen/graphql";
import { Link } from "react-router";
import LinkedInShareButton from "~/components/common/linkedin-share-button";
import { groupCertificates } from "~/lib/profile-celebration";

interface Profile {
  id?: string;
  level?: { label: string; level: number }[] | null;
  stats?: {
    achievements: { achievements: AchievementDetailedFragment[] };
  } | null;
}

export default function ProfileRight({
  profile,
  stats,
}: {
  profile: Profile;
  stats?: {
    achievements: { achievements: AchievementDetailedFragment[] };
  } | null;
}) {
  const certificateGroups = groupCertificates(
    stats?.achievements?.achievements,
  );

  const user = useUser();
  const isOwnProfile = user?.id === profile?.id;

  if (certificateGroups.length === 0) return null;

  return (
    <div className="flex flex-col gap-6">
      <Card className="flex flex-col gap-3 p-6">
        <div className="flex flex-col gap-3">
          <div className="text-sm font-semibold leading-tight">
            Credentials
          </div>
          <div className="flex flex-col gap-4">
            {certificateGroups.map((group) => {
              const iconURL = group.iconURL ?? group.objectiveIcon;

              return (
                <div
                  key={group.key}
                  className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {iconURL ? (
                      <img
                        src={iconURL}
                        alt={`${group.name} icon`}
                        className="h-12 w-16 rounded-md border border-border object-cover"
                      />
                    ) : (
                      <div className="flex h-12 w-16 items-center justify-center rounded-md border border-border bg-muted/30">
                        <AcademicCapIcon className="h-5 w-5 text-muted-foreground" />
                      </div>
                    )}
                    <div className="flex flex-col gap-1 min-w-0">
                      <div className="text-sm font-semibold leading-tight truncate">
                        {group.name}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Earned {formatDate(new Date(group.latestAwardedOn))}
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        {group.earnedCount > 1 && (
                          <Badge variant="outline">
                            Earned {group.earnedCount} times
                          </Badge>
                        )}
                        {group.share && (
                          <Link
                            to={group.share.url}
                            className="flex items-center gap-1 text-xs text-muted-foreground hover:underline"
                          >
                            See credential
                            <ArrowTopRightOnSquareIcon className="h-3 w-3" />
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>

                  {isOwnProfile && group.share && (
                    <LinkedInShareButton
                      id={group.objectiveId || group.share.achievementId}
                      name={group.name}
                      shareURL={group.share.url}
                      awardedOn={group.share.awardedOn}
                      grantId={group.share.achievementID ?? undefined}
                      variant="outline"
                      size="icon"
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </Card>
    </div>
  );
}
